/* eslint-disable no-console */
import React from "react";
import { Checkbox, Typography } from "@material-ui/core";
import {
  PathCheckboxTreeData,
  PathCheckboxTreeDefs,
  PathCheckboxTreeNode,
} from "../../types";
import { Path } from "../../helpers/path-helper";

interface Props {
  nodeId: string;
  value: string;
  label: React.ReactNode;
  data: PathCheckboxTreeData;
  defs: PathCheckboxTreeDefs;
  handleChange: (newData: PathCheckboxTreeData) => void;
}

// ツリー構造のノードをフラットにする
export const flatten = (
  nodes: PathCheckboxTreeNode[]
): PathCheckboxTreeNode[] => {
  const flattenNodes: PathCheckboxTreeNode[] = [];
  const flattenRecursive = (innerNodes: PathCheckboxTreeNode[]) => {
    innerNodes.forEach((node: PathCheckboxTreeNode) => {
      flattenNodes.push(node);
      if (node.children) {
        flattenRecursive(node.children);
      }
    });
  };
  flattenRecursive(nodes);
  return flattenNodes;
};

// パスが前方一致するnodeIdを取得する（自分は除外する）
const getDescendantNodeIds = (
  value: string,
  flattenNodes: PathCheckboxTreeNode[]
): string[] => {
  const nodeIds = flattenNodes
    .filter((node: PathCheckboxTreeNode) => {
      return (
        node.value.indexOf(value) === 0 &&
        node.value !== value &&
        node.value.split("/").length > value.split("/").length
      );
    })
    .map((node: PathCheckboxTreeNode) => {
      return node.nodeId;
    });
  return nodeIds;
};

// 兄弟ノードのnodeIdを取得する（自分含む）
// basisValue: 基準パス（＝自分）
// nodes: フラット化されたノード
const getSiblingNodeIds = (
  basisValue: string,
  flattenNodes: PathCheckboxTreeNode[]
): string[] => {
  const parentValue = Path.parent(basisValue);
  const siblingNodeIds = flattenNodes
    .filter((node: PathCheckboxTreeNode) => {
      return (
        node.value.indexOf(parentValue) === 0 &&
        node.value.split("/").length === basisValue.split("/").length
      );
    })
    .map((node: PathCheckboxTreeNode) => {
      return node.nodeId;
    });

  return siblingNodeIds;
};

// 親ノードのnodeIdを取得する（無ければnullを返却する）
// basisValue: 基準パス（＝自分）
// flattenNodes: フラット化されたノード
const getParentNodeId = (
  basisValue: string,
  flattenNodes: PathCheckboxTreeNode[]
): string | null => {
  const parentValue = Path.parent(basisValue);
  // console.log(parentValue);
  const parentNodes = flattenNodes.filter((node: PathCheckboxTreeNode) => {
    return node.value === parentValue;
  });

  if (parentNodes.length === 0) {
    // 親ノードなし
    return null;
  }

  // 親ノードのnodeIdを取得
  const parentNodeId = parentNodes[0].nodeId;
  return parentNodeId;
};

// 自分の階層から始祖に向かってチェック状態を更新する
// basisValue: 基準パス（＝自分）
// checked: チェックされたnodeIdのリスト
// flattenNodes: フラット化されたノード
const invertCascadeChecked = (
  basisValue: string,
  checked: string[],
  flattenNodes: PathCheckboxTreeNode[]
): string[] => {
  const newChecked = [...checked];
  // console.log(newChecked);
  // console.log(`basisValue:${basisValue}`);
  // console.log(flattenNodes);
  const siblingNodeIds = getSiblingNodeIds(basisValue, flattenNodes);
  const parentNodeId = getParentNodeId(basisValue, flattenNodes);
  // console.log(`siblingNodeIds:${siblingNodeIds}`);
  // console.log(`parentNodeId:${parentNodeId}`);

  if (parentNodeId === null) {
    // 親ノードがない場合処理終了
    return newChecked;
  }
  // チェックされている兄弟ノード取得
  const checkedSiblingNodeIds = siblingNodeIds.filter((siblingNodeId: string) =>
    newChecked.includes(siblingNodeId)
  );
  // console.log(`checkedSiblingNodeIds:${checkedSiblingNodeIds}`);
  if (checkedSiblingNodeIds.length === 0) {
    // チェックされている兄弟ノードなし→親ノードのチェックなし
    if (newChecked.includes(parentNodeId)) {
      newChecked.splice(newChecked.indexOf(parentNodeId), 1);
    }
  } else if (!newChecked.includes(parentNodeId)) {
    // チェックされている兄弟ノードあり→親ノードのチェックあり
    newChecked.push(parentNodeId);
  }
  const parentValue = Path.parent(basisValue);
  // 再帰処理
  return invertCascadeChecked(parentValue, newChecked, flattenNodes);
};

// 子要素を全てチェック状態にする
const processChecked = (
  nodeId: string,
  value: string,
  oldChecked: string[],
  flattenNodes: PathCheckboxTreeNode[]
): string[] => {
  let newChecked: string[] = [];
  if (!oldChecked.includes(nodeId)) {
    newChecked = [...oldChecked, nodeId];
  }

  const descendantNodeIds = getDescendantNodeIds(value, flattenNodes);
  descendantNodeIds.forEach((procId: string) => {
    if (!newChecked.includes(procId)) {
      newChecked.push(procId);
    }
  });
  return newChecked;
};

// 子要素を全て非チェック状態にする
const processUnchecked = (
  nodeId: string,
  value: string,
  oldChecked: string[],
  flattenNodes: PathCheckboxTreeNode[]
): string[] => {
  const newChecked: string[] = [];
  const descendantNodeIds = getDescendantNodeIds(value, flattenNodes);

  oldChecked.forEach((oldNodeId: string) => {
    if (oldNodeId !== nodeId && !descendantNodeIds.includes(oldNodeId)) {
      newChecked.push(oldNodeId);
    }
  });
  return newChecked;
};

const cascadeChecked = (
  nodeId: string,
  value: string,
  isChecked: boolean,
  oldChecked: string[],
  flattenNodes: PathCheckboxTreeNode[]
): string[] => {
  return isChecked
    ? processUnchecked(nodeId, value, oldChecked, flattenNodes)
    : processChecked(nodeId, value, oldChecked, flattenNodes);
};

const getNewChecked = (
  nodeId: string,
  value: string,
  isChecked: boolean,
  oldChecked: string[],
  flattenNodes: PathCheckboxTreeNode[]
): string[] => {
  const descChecked = cascadeChecked(
    nodeId,
    value,
    isChecked,
    oldChecked,
    flattenNodes
  );
  const newChecked = invertCascadeChecked(value, descChecked, flattenNodes);
  return newChecked;
};

const isIndeterminate = (
  value: string,
  flattenNodes: PathCheckboxTreeNode[],
  checked: string[]
): boolean => {
  // 子孫ノードIDを全て取得する
  // console.log(value);
  const descendantNodeIds = getDescendantNodeIds(value, flattenNodes);
  // チェックされている子孫ノードIDを全て取得する
  const checkedDescendantNodeIds = descendantNodeIds.filter(
    (descendantNodeId: string) => checked.includes(descendantNodeId)
  );
  // console.log(`isIndeterminate descendantNodeIds:[${descendantNodeIds}]`);
  // console.log(
  //   `isIndeterminate checkedDescendantNodeIds:[${checkedDescendantNodeIds}]`
  // );
  let result = false;
  // 自分がチェックされている
  if (descendantNodeIds.length > 0) {
    // 子孫ノードがある
    if (checkedDescendantNodeIds.length !== 0) {
      // 子孫ノードが１つでもチェックされている
      if (descendantNodeIds.length !== checkedDescendantNodeIds.length) {
        // 子孫ノードにチェック・非チェックが混在している
        result = true;
      }
    }
  }
  // console.log(`indeterminate: result: ${result}`);
  return result;
};

/** パスラベルチェックボックス */
export function PathLabeledCheckbox(props: Props) {
  const { nodeId, value, label, data, defs, handleChange } = props;

  // console.log("------------");
  // console.log(
  //   `LabeledCheckbox nodeId:${nodeId} data.checked:[${data.checked}]]`
  // );

  const flattenNodes = flatten(defs.nodes);
  const checked = data.checked.includes(nodeId);
  const indeterminate = isIndeterminate(value, flattenNodes, data.checked);

  //--------------------
  // コンポーネント
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Checkbox
        id={`${nodeId}`}
        checked={checked}
        indeterminate={indeterminate}
        size="small"
        style={{ padding: 0 }}
        onChange={() => {
          handleChange({
            expanded: data.expanded,
            checked: getNewChecked(
              nodeId,
              value,
              checked,
              data.checked,
              flattenNodes
            ),
          });
        }}
        onClick={(event: any) => {
          event.stopPropagation();
        }}
        color="primary"
        value={value}
      />
      <Typography variant="caption" component="span">
        {label}
      </Typography>
    </div>
  );
}
